import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { acceptInvitation } from "../../api/members";
import { fetchProject, fetchProjects, localFixtureForProject, resolveSlug, type Project } from "../../api/projects";
import type { SearchResult } from "../../api/search";
import {
  createThreadV4NotebookEntry,
  createThreadV4View,
  fetchThreadV4Notebook,
  fetchThreadV4Views,
  updateThreadV4View,
  type ThreadV4NotebookEntry,
  type ThreadV4ViewSpec,
} from "../../api/threadV4Views";
import { useAnonymous } from "../../context/AnonymousContext";
import { PlotDataProvider } from "../../context/PlotDataContext";
import { ProvenanceProvider } from "../../context/ProvenanceContext";
import { ViewStateProvider } from "../../context/ViewStateContext";
import { authClient } from "../../lib/auth";
import { checkAuthStatus, getFlow360AccessToken, getFlow360UserEmail } from "../../flow360/api";
import InterpolantProvenanceDemo from "../../pages/InterpolantProvenanceDemo";
import { useV3Data } from "../thread-v3/useV3Data";
import { rowKey } from "../thread-v4/ThreadV4Widgets";
import { isUuidLike } from "../thread-v4/viewSpecs";
import { AdbExplorerSurface } from "./ThreadV5AdbExplorer";
import { TrainingSurface } from "./ThreadV5TrainingSurface";
import { AdaptiveCampaignProvider } from "../adaptive-sampling/campaign-context";
import AppacWakeWorkbench from "../appac-wake";
import { Flow360ImportSurface } from "./ThreadV5Flow360Import";
import { HighliftMultifidelitySurface } from "./HighliftMultifidelitySurface";
import { FlexcomputeOnlyGuard } from "../decks/components/FlexcomputeOnlyGuard";
import { WingSectioningSurface } from "./WingSectioningSurface";
import { AircraftSurface } from "./AircraftSurface";
import { ControlSurfacesSurface } from "./ControlSurfacesSurface";
import { AirshowCoverageSurface } from "../parametric-geometry/AirshowCoverageSurface";
import { ParametricGeometrySurface } from "../parametric-geometry/ParametricGeometrySurface";
import { ThreadV5StartMenu } from "./ThreadV5StartMenu";
import { SelvedgeDemoSurface } from "./SelvedgeDemoSurface";
import { KestrelGraphSurface } from "./KestrelGraphSurface";
import { DagViewerSurface, type DagViewerNode } from "../dag-viewer";
import { parseDagWorkspaceState } from "../dag-viewer/viewState";
import {
  LINEAGE_LAB_PROJECT_NAME,
  LINEAGE_LAB_PROJECT_PATH,
  LineageWorkspaceSurface,
  isLineageLabProjectId,
} from "../lineage-lab";
import {
  ArtifactsSurface,
  BranchingSurface,
  type DashboardSaveRequest,
  DashboardEditorSurface,
  DashboardLeaveGuardModal,
  DashboardViewSurface,
  DataSurface,
  FocusLayer,
  type Flow360ConnectorSummary,
  HomeSurface,
  LineageFullSurface,
  NotebookDrawer,
  ProjectChatDrawer,
  ProjectMembersSurface,
  ProjectsSurface,
  ThreadV5Shell,
  WorkbenchSurface,
} from "./ThreadV5Surfaces";
import { useProjectChat, useThreadPresence } from "./useV5Realtime";
import { useUndoRedoStore } from "./undoRedoStore";
import { useIsV5Mobile } from "../../hooks/useMediaQuery";
import { ThreadV5MobileShell } from "./ThreadV5MobileShell";
import V5TourLauncher from "./tour/V5TourLauncher";
import type { V5TourLive } from "./tour/v5TourTypes";
import "./thread-v5.css";
import "./thread-v5-mobile.css";

// FXC-8630: "lineage" is the new first-class full-surface lineage view.
// FXC-8640: "provenance" kept in the union so legacy URLs/params still parse; viewFromParam redirects it to "lineage".
export type V5View = "projects" | "workbench" | "home" | "data" | "artifacts" | "provenance" | "branching" | "members" | "dashboard-edit" | "tailor" | "filament-model" | "flow360-import" | "lineage" | "interpolant-audit" | "selvedge" | "kestrel-graph" | "composite" | "highlift" | "parametric-geometry" | "airshow-coverage" | "wing-section" | "aircraft" | "control-surfaces" | "training";
export type V5Focus = "artifact" | "flowfield" | "dag" | "geometry" | "notebook" | null;
export type V5FlowfieldPair = "artifact" | "geometry" | "dag" | "table" | "correlogram" | null;
type V5Drawer = "spool" | "chat" | null;
type SlugResolution = { slug: string; status: "pending" } | { slug: string; status: "resolved"; id: string } | { slug: string; status: "unresolved" };
export type V5TableMode = "table" | "correlogram";
export type V5PlotSize = "large" | null;

const V5_VIEWS = new Set<V5View>(["projects", "workbench", "home", "data", "artifacts", "provenance", "branching", "members", "dashboard-edit", "tailor", "filament-model", "flow360-import", "lineage", "interpolant-audit", "selvedge", "kestrel-graph", "composite", "highlift", "parametric-geometry", "airshow-coverage", "wing-section", "aircraft", "control-surfaces", "training"]);
// FXC-8719: views that need an account (project listing, membership, imports, board
// editing, and the auth-backed geometry/training surfaces — their APIs 401 for
// anonymous viewers). Anonymous public viewers are routed back to the workbench instead.
const ANON_BLOCKED_VIEWS = new Set<V5View>(["projects", "members", "dashboard-edit", "filament-model", "flow360-import", "parametric-geometry", "airshow-coverage", "wing-section", "aircraft", "control-surfaces", "highlift", "training"]);
const V5_FOCI = new Set<Exclude<V5Focus, null>>(["artifact", "flowfield", "dag", "geometry", "notebook"]);
const V5_DRAWERS = new Set<Exclude<V5Drawer, null>>(["spool", "chat"]);
const CLEARED_RUN_PARAM = "none";

function requestedProjectId(routeProjectId: string | undefined, searchParams: URLSearchParams): string | undefined {
  return routeProjectId ?? searchParams.get("project") ?? undefined;
}

function viewFromParam(value: string | null, fallback: V5View, keepFlow360ImportView = false): V5View {
  // FXC-8640: "provenance" view retired — redirect any legacy URL param to "lineage".
  if (value === "provenance") return "lineage";
  if (value === "flow360-import" && !keepFlow360ImportView) return "filament-model";
  return value && V5_VIEWS.has(value as V5View) ? (value as V5View) : fallback;
}

function focusFromParam(value: string | null): V5Focus {
  return value && V5_FOCI.has(value as Exclude<V5Focus, null>) ? (value as V5Focus) : null;
}

function focusFromSearchParams(searchParams: URLSearchParams): V5Focus {
  const panel = searchParams.get("panel");
  if (panel === "viewer") return "flowfield";
  if (panel === "dag") return "dag";
  if (panel === "geometry") return "geometry";
  return focusFromParam(searchParams.get("focus"));
}

function drawerFromSearchParams(searchParams: URLSearchParams): V5Drawer {
  const drawer = searchParams.get("drawer");
  if (drawer === "notebook") return "spool";
  if (drawer && V5_DRAWERS.has(drawer as Exclude<V5Drawer, null>)) return drawer as Exclude<V5Drawer, null>;
  return searchParams.get("focus") === "notebook" ? "spool" : null;
}

function runFromSearchParams(searchParams: URLSearchParams): string | null {
  const run = searchParams.get("run");
  return run && run !== CLEARED_RUN_PARAM ? run : null;
}

function typedOperationRunId(value: string): string | null {
  const prefix = "operation_run:";
  if (!value.startsWith(prefix)) return null;
  const rawId = value.slice(prefix.length).trim();
  return isUuidLike(rawId) ? rawId : null;
}

function rawOperationRunId(value: string): string | null {
  const rawId = value.trim();
  return isUuidLike(rawId) ? rawId : null;
}

/** Resolve an activation only when its canonical operation-run identity agrees end-to-end. */
function operationRunIdFromLineageActivation(id: string, node?: DagViewerNode): string | null {
  if (!node) return typedOperationRunId(id);
  if (node.metadata.canonical_entity_kind !== "operation_run") return null;

  const nodeRunId = typedOperationRunId(node.id);
  const canonicalId = node.metadata.canonical_id;
  if (!nodeRunId || typeof canonicalId !== "string") return null;

  const metadataRunId = typedOperationRunId(canonicalId) ?? rawOperationRunId(canonicalId);
  const activationRunId = typedOperationRunId(id) ?? rawOperationRunId(id);
  return metadataRunId === nodeRunId && activationRunId === nodeRunId ? nodeRunId : null;
}

function plotSizeFromSearchParams(searchParams: URLSearchParams): V5PlotSize {
  return searchParams.get("plot") === "large" ? "large" : null;
}

function flowfieldPairFromSearchParams(
  searchParams: URLSearchParams,
  activeView: V5View,
  tableMode: V5TableMode,
  focusedAssetId: string | null,
): V5FlowfieldPair {
  if (searchParams.get("panel") !== "viewer") return null;
  const previousFocus = focusFromParam(searchParams.get("focus"));
  if (previousFocus === "artifact" && focusedAssetId) return "artifact";
  if (previousFocus === "geometry" || previousFocus === "dag") return previousFocus;
  if (focusedAssetId) return "artifact";
  if (activeView === "workbench" || activeView === "data") {
    return tableMode === "correlogram" ? "correlogram" : "table";
  }
  return null;
}

function initialsFromIdentity(identity: string | null | undefined): string {
  const trimmed = (identity ?? "").trim();
  if (!trimmed) return "UN";
  const label = trimmed.includes("@") ? trimmed.split("@")[0] ?? trimmed : trimmed;
  const parts = label.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1
    ? `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`
    : label.slice(0, 2);
  return (letters || "UN").toUpperCase();
}

function storedFlow360Email(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem("flow360_user_email");
  } catch {
    return null;
  }
}

function storedFlow360Flag(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function localStorageKey(projectId: string, branch: string, kind: "views" | "notebook"): string {
  return `thread:v5:local:${projectId}:${branch}:${kind}`;
}

function readLocalNotebook(projectId: string, branch: string): ThreadV4NotebookEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(localStorageKey(projectId, branch, "notebook")) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalNotebook(projectId: string, branch: string, entries: ThreadV4NotebookEntry[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(localStorageKey(projectId, branch, "notebook"), JSON.stringify(entries));
}

function readLocalViews(projectId: string, branch: string): ThreadV4ViewSpec[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(localStorageKey(projectId, branch, "views")) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalViews(projectId: string, branch: string, views: ThreadV4ViewSpec[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(localStorageKey(projectId, branch, "views"), JSON.stringify(views));
}

/** Clear the in-progress layout draft written by DashboardViewSurface for this view. */
function clearDashboardLayoutDraft(projectId: string, branch: string, viewId: string | undefined): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(`thread:v5:local:${projectId}:${branch}:layout-draft:${viewId ?? "new"}`);
  } catch {
    // ignore
  }
}

function makeLocalNotebookEntry(projectId: string, branch: string, body: string, selectedRunId: string | null): ThreadV4NotebookEntry {
  return {
    id: `v5-local-note-${Date.now().toString(36)}`,
    project_id: projectId,
    view_id: null,
    branch,
    entry_type: "note",
    author_id: "local-user",
    author_label: "You",
    owner_user_id: "local-user",
    visibility: "work_shared",
    source_type: "manual",
    source_uri: null,
    source_external_id: null,
    source_created_at: null,
    captured_at: new Date().toISOString(),
    body,
    body_format: "markdown",
    anchors: selectedRunId ? { run_id: selectedRunId } : { project_id: projectId },
    source_metadata: {},
    metadata: { surface: "thread-v5" },
    created_at: new Date().toISOString(),
    updated_at: null,
  };
}

function makeDashboardView(
  projectId: string,
  branch: string,
  name: string,
  selectedRunId: string | null,
  overrides: Partial<Pick<ThreadV4ViewSpec, "layout_json" | "widget_state_json" | "binding_json" | "metadata">> = {},
): ThreadV4ViewSpec {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "v5-board";
  const suffix = Date.now().toString(36).slice(-5);
  return {
    id: `v5-local-view-${Date.now().toString(36)}`,
    project_id: projectId,
    branch,
    name,
    slug: `${slug}-${suffix}`,
    category: "custom",
    visibility: "project",
    owner_id: "local-user",
    layout_json: overrides.layout_json ?? {
      schema_version: "thread.view.layout.v1",
      mode: "dashboard",
      cols: 12,
      row_height: 96,
      widgets: [
        { id: "runs", kind: "table", title: "Simulation runs", position: { x: 0, y: 0, w: 7, h: 4 } },
        { id: "artifacts", kind: "asset-rail", title: "Artifact shelf", position: { x: 7, y: 0, w: 3, h: 4, dock: "right" } },
        { id: "dag", kind: "dag", title: "DAG minimap", position: { x: 0, y: 4, w: 3, h: 2 } },
        { id: "flowfield", kind: "flowfield", title: "Flowfield", position: { x: 10, y: 0, w: 2, h: 4, dock: "right" } },
        { id: "notebook", kind: "notebook", title: "Spool", position: { x: 3, y: 4, w: 4, h: 2, dock: "bottom" } },
      ],
    },
    widget_state_json: overrides.widget_state_json ?? {},
    binding_json: overrides.binding_json ?? (selectedRunId ? { selected_run_id: selectedRunId } : {}),
    metadata: { source: "thread-v5", design: "Thread V5", ...(overrides.metadata ?? {}) },
    thumbnail_asset_id: null,
    is_default: false,
    created_at: new Date().toISOString(),
    updated_at: null,
  };
}

function InterpolantAuditSurface({
  projectName,
  branch,
  selectedRunId,
}: {
  projectName: string;
  branch: string;
  selectedRunId: string | null;
}) {
  const runLabel = selectedRunId ?? "surrogate-audit-012";
  return (
    <main className="v5-main v5-interpolant-audit-main" data-testid="v5-interpolant-audit-screen">
      <header className="v5-page-header v5-interpolant-audit-header">
        <div>
          <span className="v5-eyebrow">Audit</span>
          <h1>Audit</h1>
          <p>Review derived control-margin data points and the interpolant support evidence they inherit from CFD.</p>
        </div>
        <div className="v5-interpolant-audit-records" aria-label="Audit record count">
          <span><strong>3</strong> aDB points</span>
          <span><strong>12D</strong> surrogate</span>
          <span><strong>3D</strong> reduced view</span>
        </div>
      </header>

      <section className="v5-panel v5-interpolant-audit-panel" aria-label="Audit">
        <div className="v5-interpolant-audit-body">
          <section className="v5-interpolant-audit-stage" aria-label="Interpolant provenance animation">
            <InterpolantProvenanceDemo embedded compact initialMapView="3d" />
          </section>

          <aside className="v5-interpolant-audit-inspector" aria-label="Audit inspector">
            <section>
              <span className="v5-eyebrow">Selected data points</span>
              <h3>Control margin audit records</h3>
              <p>Three certification aDB scalars at separate flight states: inside the envelope, on the support boundary, and extrapolated.</p>
              <div className="v5-interpolant-status-stack">
                <span><i className="is-interpolated" />in envelope</span>
                <span><i className="is-boundary" />boundary</span>
                <span><i className="is-extrapolated" />extrapolated</span>
              </div>
            </section>
            <section>
              <span className="v5-eyebrow">Per-value pedigree</span>
              <dl>
                <div><dt>Project</dt><dd>{projectName}</dd></div>
                <div><dt>Branch</dt><dd>{branch}</dd></div>
                <div><dt>Run</dt><dd>{runLabel}</dd></div>
                <div><dt>Source</dt><dd>12D aircraft response surrogate</dd></div>
                <div><dt>Operation</dt><dd>derived control margin</dd></div>
              </dl>
            </section>
            <section>
              <span className="v5-eyebrow">Audit evidence</span>
              <ul>
                <li>Reduced coordinates show where the query sits relative to CFD support.</li>
                <li>Each aDB point carries the inherited interpolation or extrapolation verdict.</li>
                <li>Downstream quantities remain traceable to source support cases.</li>
              </ul>
            </section>
            <section>
              <span className="v5-eyebrow">Records</span>
              <p className="v5-interpolant-record-list">aDB-CERT-042.CM_MARGIN / aDB-CERT-057.CM_MARGIN / aDB-CERT-089.CM_MARGIN</p>
            </section>
          </aside>
        </div>
        <footer className="v5-lineage-footer v5-interpolant-audit-footer">
          One certification scalar per beat - Thread carries the support classification from the source interpolant into every downstream artifact.
        </footer>
      </section>
    </main>
  );
}

function SelvedgeSurface({ isAnonymous }: { isAnonymous: boolean }) {
  return <SelvedgeDemoSurface isAnonymous={isAnonymous} />;
}

export default function ThreadV5WorkbenchPage() {
  const { projectId: routeProjectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // FXC-8719: true only under the /public/:projectId route (AnonymousProvider).
  // Hides write affordances; the API enforces read-only for anonymous callers.
  const { isAnonymous } = useAnonymous();
  // FXC-8939: below ~768px swap the desktop topbar + nav rail for the mobile shell.
  const isMobile = useIsV5Mobile();
  const isTailorMannequinDemo = searchParams.get("tailorDemo") === "1";
  const isInterpolantAuditRoute = location.pathname === "/v5/interpolant-provenance";
  const isSelvedgeRoute = location.pathname === "/v5/selvedge-demo";
  const isKestrelGraphRoute = location.pathname === "/v5/kestrel-graph";
  // Selvedge searches globally, so it deliberately has no synthetic project.
  const rawRouteProject = isInterpolantAuditRoute ? "x57_maxwell" : isSelvedgeRoute ? undefined : requestedProjectId(routeProjectId, searchParams);
  // A project SLUG in the URL (e.g. /v5/aer-2026-wingbody-v7) is resolved to its
  // canonical UUID so every surface + API call uses the real id. The wing/highlift
  // routes type project_id as a UUID and reject a raw slug with a 422; UUID-like ids
  // and local fixtures are used as-is, and an unresolvable slug (private/non-member,
  // or unknown) falls through unchanged — write surfaces gate on a persisted id.
  const slugToResolve =
    !isInterpolantAuditRoute && !isSelvedgeRoute &&
    rawRouteProject &&
    !isLineageLabProjectId(rawRouteProject) &&
    !isUuidLike(rawRouteProject) &&
    !localFixtureForProject(rawRouteProject)
      ? rawRouteProject
      : null;
  const [slugResolution, setSlugResolution] = useState<SlugResolution | null>(null);
  useEffect(() => {
    if (!slugToResolve) {
      setSlugResolution(null);
      return;
    }
    let cancelled = false;
    setSlugResolution({ slug: slugToResolve, status: "pending" });
    resolveSlug(slugToResolve)
      .then((id) => { if (!cancelled) setSlugResolution({ slug: slugToResolve, status: "resolved", id }); })
      .catch(() => { if (!cancelled) setSlugResolution({ slug: slugToResolve, status: "unresolved" }); });
    return () => { cancelled = true; };
  }, [slugToResolve]);
  const slugResolvePending = Boolean(slugToResolve && (slugResolution?.slug !== slugToResolve || slugResolution.status === "pending"));
  const routeProject =
    slugToResolve && slugResolution?.slug === slugToResolve
      ? slugResolution.status === "resolved"
        ? slugResolution.id
        : slugResolution.status === "unresolved"
          ? rawRouteProject
          : undefined
      : slugToResolve
        ? undefined
        : rawRouteProject;
  const lineageLabProject = isLineageLabProjectId(routeProject);
  const requestedBranch = searchParams.get("branch");
  const inviteParam = searchParams.get("invite");
  const dataSearchParams = new URLSearchParams(searchParams);
  for (const key of ["view", "focus", "run", "artifact", "dashboard", "panel", "configTab", "plot", "nodes", "invite", "drawer", "tour", "kiosk"]) dataSearchParams.delete(key);
  for (const key of [...dataSearchParams.keys()]) {
    if (key.startsWith("tailor") || key.startsWith("dag") || key.startsWith("lab")) dataSearchParams.delete(key);
  }
  const requestedView = searchParams.get("view");
  const lineageOnlyLoad = requestedView === "lineage" || requestedView === "provenance";
  const v3State = useV3Data(routeProject, requestedBranch, dataSearchParams.toString(), {
    projectMetadataOnly: lineageOnlyLoad,
    enabled: !slugResolvePending,
  });
  const data = v3State.data;
  const isUnscopedTailorDemo = isTailorMannequinDemo && !routeProject;
  const projectId = slugResolvePending
    ? "resolving-thread-project"
    : routeProject ?? (isUnscopedTailorDemo ? "tailor-mannequin-demo" : data.project?.id ?? "local-thread-project");
  const branch = slugResolvePending
    ? requestedBranch || "main"
    : lineageLabProject
    ? "synthetic"
    : isUnscopedTailorDemo
    ? requestedBranch || "thread-dev"
    : data.branch || requestedBranch || "main";
  const persistedProject = !slugResolvePending && isUuidLike(projectId);
  const [projectSummary, setProjectSummary] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectsRefreshKey, setProjectsRefreshKey] = useState(0);
  const fixtureName = searchParams.get("fixture");
  const projectName = slugResolvePending
    ? "Resolving Thread project"
    : lineageLabProject
      ? LINEAGE_LAB_PROJECT_NAME
      : isUnscopedTailorDemo
        ? "Tailor · Mannequin aircraft ADB"
        : isSelvedgeRoute
          ? "Selvedge plot recognition"
          : (projectSummary?.id === projectId ? projectSummary.name : null)
            ?? data.project?.name
            ?? (fixtureName ? `${fixtureName} fixture` : "Local Thread data");
  const defaultView: V5View = isKestrelGraphRoute ? "kestrel-graph" : isSelvedgeRoute ? "selvedge" : isInterpolantAuditRoute ? "interpolant-audit" : routeProject ? "workbench" : "projects";
  const [activeView, setActiveView] = useState<V5View>(() => viewFromParam(searchParams.get("view"), defaultView, true));
  const [focus, setFocus] = useState<V5Focus>(() => focusFromSearchParams(searchParams));
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => runFromSearchParams(searchParams));
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(() => searchParams.get("nodes"));
  const [focusedAssetId, setFocusedAssetId] = useState<string | null>(() => searchParams.get("artifact"));
  const [tableMode, setTableMode] = useState<V5TableMode>(() => (searchParams.get("mode") === "correlogram" ? "correlogram" : "table"));
  const [plotSize, setPlotSize] = useState<V5PlotSize>(() => plotSizeFromSearchParams(searchParams));
  const [notebookEntries, setNotebookEntries] = useState<ThreadV4NotebookEntry[]>([]);
  const [notebookDraft, setNotebookDraft] = useState("");
  const [chatDraft, setChatDraft] = useState("");
  const [views, setViews] = useState<ThreadV4ViewSpec[]>([]);
  const routeScopedNotebookEntries = slugResolvePending ? [] : notebookEntries;
  const routeScopedViews = slugResolvePending ? [] : views;
  const [activeDashboardName, setActiveDashboardName] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState("UN");
  const [flow360ConnectorRefreshKey, setFlow360ConnectorRefreshKey] = useState(0);
  const [flow360AuthStatus, setFlow360AuthStatus] = useState<Awaited<ReturnType<typeof checkAuthStatus>> | null>(null);
  const [acceptingProjectId, setAcceptingProjectId] = useState<string | null>(null);
  const [acceptInviteError, setAcceptInviteError] = useState<string | null>(null);
  const inviteAcceptAttemptedRef = useRef<string | null>(null);

  /**
   * FXC-8690: tracks whether DashboardViewSurface has unsaved layout edits.
   * Used to gate in-app navigation and register the beforeunload handler.
   */
  const [dashboardDirty, setDashboardDirty] = useState(false);
  const dashboardDirtyRef = useRef(dashboardDirty);
  dashboardDirtyRef.current = dashboardDirty;
  // Ref for the active dashboard view id — needed inside useCallbacks that are declared before activeDashboardView.
  const activeDashboardViewIdRef = useRef<string | undefined>(undefined);
  // Live workbench state for tour-challenge validators (refreshed every render below).
  const tourLiveRef = useRef<V5TourLive>({ selectedRunId: null, focus: null, activeDashboard: null });
  // The dashboard surface registers its persist function here for the leave-guard modal's "Save and leave".
  const dashboardSaveRef = useRef<(() => void) | null>(null);
  // When the user picks "Save and leave", the post-save openDashboard redirect must NOT
  // pull them back to the dashboard — the captured pendingNav should win instead.
  const suppressDashboardRedirectRef = useRef(false);
  // FXC-8690 (follow-up): the in-app leave-guard now uses a styled V5 modal instead of window.confirm.
  // We capture the intended navigation target so the user's modal choice can run it (or be cancelled).
  type PendingNav =
    | { kind: "view"; view: V5View }
    | { kind: "projects" };
  const [pendingNav, setPendingNav] = useState<PendingNav | null>(null);

  // Tab-close / reload guard: warn when there are unsaved dashboard edits.
  useEffect(() => {
    if (!dashboardDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dashboardDirty]);

  // Workbench-wide undo/redo. Cmd/Ctrl+Z undoes, Cmd/Ctrl+Shift+Z (or Ctrl+Y)
  // redoes, acting on whatever surface scope is active (see undoRedoStore). We
  // mirror the "/" search handler's guard: skip while typing in a field, and
  // only preventDefault when there's actually something to undo/redo so native
  // text-field undo still works elsewhere.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "z" && event.key.toLowerCase() !== "y") return;
      if (!event.metaKey && !event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;

      const store = useUndoRedoStore.getState();
      const isRedo = (event.key.toLowerCase() === "z" && event.shiftKey) || event.key.toLowerCase() === "y";
      if (isRedo) {
        if (!store.canRedo()) return;
        event.preventDefault();
        store.redo();
      } else {
        if (event.shiftKey) return;
        if (!store.canUndo()) return;
        event.preventDefault();
        store.undo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (isAnonymous) return;
    let cancelled = false;
    const applyIdentity = (session: { user?: { email?: string; id?: string } } | null) => {
      if (cancelled) return;
      setUserInitials(initialsFromIdentity(session?.user?.email ?? storedFlow360Email() ?? session?.user?.id));
    };

    authClient.auth.getSession().then(({ data: { session } }) => applyIdentity(session));
    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((_event, session) => applyIdentity(session));
    const refreshFromFlow360 = () => applyIdentity(null);
    window.addEventListener("flow360-auth-ready", refreshFromFlow360);
    window.addEventListener("storage", refreshFromFlow360);
    return () => {
      cancelled = true;
      subscription.unsubscribe();
      window.removeEventListener("flow360-auth-ready", refreshFromFlow360);
      window.removeEventListener("storage", refreshFromFlow360);
    };
  }, [isAnonymous]);

  useEffect(() => {
    if (isAnonymous) return;
    let cancelled = false;
    checkAuthStatus()
      .then((status) => {
        if (!cancelled) setFlow360AuthStatus(status);
      })
      .catch(() => {
        if (!cancelled) setFlow360AuthStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [flow360ConnectorRefreshKey, isAnonymous]);

  useEffect(() => {
    const refreshFlow360Connector = () => setFlow360ConnectorRefreshKey((key) => key + 1);
    window.addEventListener("flow360-auth-ready", refreshFlow360Connector);
    window.addEventListener("storage", refreshFlow360Connector);
    return () => {
      window.removeEventListener("flow360-auth-ready", refreshFlow360Connector);
      window.removeEventListener("storage", refreshFlow360Connector);
    };
  }, []);

  useEffect(() => {
    setActiveView(viewFromParam(searchParams.get("view"), defaultView, true));
    setFocus(focusFromSearchParams(searchParams));
    setSelectedRunId(runFromSearchParams(searchParams));
    setSelectedNodeId(searchParams.get("nodes"));
    setFocusedAssetId(searchParams.get("artifact"));
    setTableMode(searchParams.get("mode") === "correlogram" ? "correlogram" : "table");
    setPlotSize(plotSizeFromSearchParams(searchParams));
    setActiveDashboardName(searchParams.get("dashboard"));
  }, [defaultView, searchParams]);

  useEffect(() => {
    // Anonymous viewers cannot list projects (401) — the projects surface is
    // blocked for them anyway (ANON_BLOCKED_VIEWS), so skip the fetch.
    if (isAnonymous) {
      setProjects([]);
      setProjectsLoading(false);
      setProjectsError(null);
      return;
    }
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    fetchProjects()
      .then((items) => {
        if (cancelled) return;
        setProjects(items);
        setProjectsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setProjects([]);
        setProjectsError(error instanceof Error ? error.message : "Unable to load projects");
        setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAnonymous, projectsRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    if (!persistedProject) {
      setProjectSummary(null);
      return;
    }
    fetchProject(projectId)
      .then((project) => {
        if (!cancelled) setProjectSummary(project);
      })
      .catch(() => {
        if (!cancelled) setProjectSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [persistedProject, projectId]);

  useEffect(() => {
    if (isAnonymous) return;
    if (!routeProject || inviteParam !== "1") return;
    const inviteKey = `${routeProject}:1`;
    if (inviteAcceptAttemptedRef.current === inviteKey) return;
    inviteAcceptAttemptedRef.current = inviteKey;

    let cancelled = false;
    acceptInvitation(routeProject)
      .catch(() => {
        // Already-accepted invites can reject because there is no pending row.
        // The normal project load still determines whether the signed-in user has access.
      })
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete("invite");
        setSearchParams(next, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [inviteParam, isAnonymous, routeProject, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    if (slugResolvePending) {
      setViews([]);
      return;
    }
    if (!persistedProject) {
      setViews(readLocalViews(projectId, branch));
      return;
    }
    fetchThreadV4Views(projectId, branch)
      .then(async (response) => {
        let next = response.views.filter((view) => view.category !== "standard");
        // Saved/seeded dashboards live on branch "main"; useV3Data.selectBranch can
        // auto-pick another branch, which would empty the LAYOUTS rail. When the
        // selected branch has no views, fall back to the main-branch views.
        if (next.length === 0 && branch !== "main") {
          const fallback = await fetchThreadV4Views(projectId, "main");
          next = fallback.views.filter((view) => view.category !== "standard");
        }
        if (!cancelled) setViews(next);
      })
      .catch(() => {
        if (!cancelled) setViews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branch, persistedProject, projectId, slugResolvePending]);

  useEffect(() => {
    let cancelled = false;
    if (slugResolvePending) {
      setNotebookEntries([]);
      return;
    }
    if (!persistedProject) {
      setNotebookEntries(readLocalNotebook(projectId, branch));
      return;
    }
    fetchThreadV4Notebook(projectId, branch)
      .then((response) => {
        if (!cancelled) setNotebookEntries(response.entries);
      })
      .catch(() => {
        if (!cancelled) setNotebookEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branch, persistedProject, projectId, slugResolvePending]);

  const updateRoute = useCallback((patch: { view?: V5View; branch?: string; focus?: V5Focus; drawer?: V5Drawer; run?: string | null; clearRun?: boolean; artifact?: string | null; dashboard?: string | null; mode?: V5TableMode | null; nodes?: string | null; panel?: string | null; configTab?: string | null; plot?: V5PlotSize; aircraft?: string | null }, options: { replace?: boolean } = {}) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      // Branch lives on the same `?branch=` param the whole workbench reads. Routing
      // it through updateRoute (rather than a separate useSearchParams setter) means a
      // branch switch that ALSO changes view/run happens in ONE navigation — two
      // sibling setSearchParams calls in the same tick race on React Router 7 and the
      // second (from a different hook instance) clobbers the first (FXC-9009 freeze).
      if (patch.branch !== undefined) {
        if (patch.branch && patch.branch !== "main") params.set("branch", patch.branch);
        else params.delete("branch");
      }
      if (patch.view !== undefined) {
        if (patch.view === "workbench") params.delete("view");
        else params.set("view", patch.view);
      }
      if (patch.focus !== undefined) {
        if (patch.focus) params.set("focus", patch.focus);
        else params.delete("focus");
      }
      if (patch.drawer !== undefined) {
        if (patch.drawer) params.set("drawer", patch.drawer);
        else params.delete("drawer");
        if (patch.drawer) params.delete("focus");
      }
      if (patch.run !== undefined) {
        if (patch.clearRun) params.set("run", CLEARED_RUN_PARAM);
        else if (patch.run) params.set("run", patch.run);
        else params.delete("run");
      } else if (patch.clearRun) {
        params.set("run", CLEARED_RUN_PARAM);
      }
      if (patch.artifact !== undefined) {
        if (patch.artifact) params.set("artifact", patch.artifact);
        else params.delete("artifact");
      }
      if (patch.dashboard !== undefined) {
        if (patch.dashboard) params.set("dashboard", patch.dashboard);
        else params.delete("dashboard");
      }
      if (patch.mode !== undefined) {
        if (patch.mode && patch.mode !== "table") params.set("mode", patch.mode);
        else params.delete("mode");
      }
      if (patch.nodes !== undefined) {
        if (patch.nodes) params.set("nodes", patch.nodes);
        else params.delete("nodes");
      }
      if (patch.panel !== undefined) {
        if (patch.panel) params.set("panel", patch.panel);
        else params.delete("panel");
      }
      if (patch.configTab !== undefined) {
        if (patch.configTab) params.set("configTab", patch.configTab);
        else params.delete("configTab");
      }
      if (patch.plot !== undefined) {
        if (patch.plot) params.set("plot", patch.plot);
        else params.delete("plot");
      }
      if (patch.aircraft !== undefined) {
        if (patch.aircraft) params.set("aircraft", patch.aircraft);
        else params.delete("aircraft");
      }
      return params;
    }, { replace: options.replace ?? false });
  }, [setSearchParams]);

  const selectFilamentAircraft = useCallback((designId: string) => {
    updateRoute({ aircraft: designId }, { replace: true });
  }, [updateRoute]);

  useEffect(() => {
    // Only reconcile once data has settled — never re-pin against a half-loaded
    // (or previous-branch) row set.
    if (v3State.status === "loading") return;
    if (
      !routeProject
      || lineageOnlyLoad
      || activeView === "projects"
      || activeView === "lineage"
      || activeView === "provenance"
      || activeView === "interpolant-audit"
      || activeView === "selvedge"
      || activeView === "kestrel-graph"
      || !data.rows[0]
    ) return;
    // Explicitly cleared selection (run=none) is a deliberate state — leave it.
    if (searchParams.get("run") === CLEARED_RUN_PARAM) return;
    // FXC-9009: reconcile a stale pinned run. A run pinned on one branch (via a
    // deep link or carried across a branch switch) may not exist on the loaded
    // branch. Leaving it pinned made the workbench resolve a phantom selection
    // (the live branch-switch freeze). If the pinned run isn't a real row here,
    // fall through and re-pin the first real row on this branch; only bail when a
    // valid run is already selected.
    if (selectedRunId && data.rows.some((row) => rowKey(row) === selectedRunId)) return;
    if ((data.totalRowCount || data.rows.length) > 0) {
      const firstRow = data.rows[0];
      updateRoute({ run: rowKey(firstRow), nodes: firstRow.rowNodeId ?? null }, { replace: true });
    }
  }, [activeView, data.rows, data.totalRowCount, lineageOnlyLoad, routeProject, searchParams, selectedRunId, updateRoute, v3State.status]);

  // FXC-8690: the actual route changes, performed once the leave-guard (if any) has resolved.
  const performOpenView = useCallback((view: V5View) => {
    updateRoute({ view, focus: null, drawer: null, dashboard: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  const performOpenProjects = useCallback(() => {
    updateRoute({ view: "projects", focus: null, drawer: null, run: null, artifact: null, dashboard: null, nodes: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  const openView = useCallback((view: V5View) => {
    // FXC-8690: when the dashboard editor has unsaved changes, capture the target and
    // open the styled leave-guard modal instead of navigating immediately.
    if (dashboardDirtyRef.current) {
      setPendingNav({ kind: "view", view });
      return;
    }
    performOpenView(view);
  }, [performOpenView]);

  const openProjects = useCallback(() => {
    if (dashboardDirtyRef.current) {
      setPendingNav({ kind: "projects" });
      return;
    }
    performOpenProjects();
  }, [performOpenProjects]);

  // FXC-9009: switch branch atomically. One navigation sets the branch, returns to
  // the workbench, and DROPS the pinned run/nodes/artifact — the pinned selection is
  // per-branch, so carrying a run that doesn't exist on the target branch left the
  // workbench resolving a phantom selection (the live branch-switch freeze). The
  // auto-select effect then picks a valid run on the new branch. Routed through the
  // single updateRoute setter so the branch + view change never race.
  const switchBranch = useCallback((nextBranch: string) => {
    updateRoute({ branch: nextBranch, view: "workbench", run: null, nodes: null, artifact: null, focus: null, dashboard: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  // FXC-8690: run the captured pending navigation after the user resolves the modal.
  const runPendingNav = useCallback((target: PendingNav) => {
    if (target.kind === "view") performOpenView(target.view);
    else performOpenProjects();
  }, [performOpenView, performOpenProjects]);

  // Modal action: persist the dashboard, then proceed to the captured target.
  const handleLeaveGuardSave = useCallback(() => {
    const target = pendingNav;
    // Suppress the post-save openDashboard redirect so the captured nav target wins,
    // even when the persist call resolves asynchronously.
    suppressDashboardRedirectRef.current = true;
    dashboardSaveRef.current?.();
    setDashboardDirty(false);
    setPendingNav(null);
    if (target) runPendingNav(target);
  }, [pendingNav, runPendingNav]);

  // Modal action: discard the draft, then proceed to the captured target.
  const handleLeaveGuardDiscard = useCallback(() => {
    const target = pendingNav;
    clearDashboardLayoutDraft(projectId, branch, activeDashboardViewIdRef.current);
    setDashboardDirty(false);
    setPendingNav(null);
    if (target) runPendingNav(target);
  }, [pendingNav, projectId, branch, runPendingNav]);

  // Modal action: cancel — stay on the current view, drop the captured target.
  const handleLeaveGuardCancel = useCallback(() => {
    setPendingNav(null);
  }, []);

  // Stable callback the dashboard surface uses to register/unregister its persist function.
  const registerDashboardSave = useCallback((save: (() => void) | null) => {
    dashboardSaveRef.current = save;
  }, []);

  const openMembers = useCallback(() => {
    updateRoute({ view: "members", focus: null, drawer: null, panel: null, configTab: null, plot: null });
  }, [updateRoute]);

  const openProject = useCallback((project: Project) => {
    const params = new URLSearchParams(location.search);
    for (const key of ["view", "focus", "drawer", "run", "artifact", "dashboard", "mode", "project", "fixture", "nodes", "invite"]) {
      params.delete(key);
    }
    for (const key of [...params.keys()]) {
      if (key.startsWith("dag") || key.startsWith("lab")) params.delete(key);
    }
    if (!params.has("source")) params.set("source", "run-table");
    if (!params.has("graph")) params.set("graph", "1");
    const query = params.toString();
    navigate(`/v5/${project.id}${query ? `?${query}` : ""}`);
  }, [location.search, navigate]);

  const openLineageLab = useCallback(() => {
    navigate(LINEAGE_LAB_PROJECT_PATH);
  }, [navigate]);

  const acceptListedProjectInvite = useCallback(async (project: Project) => {
    setAcceptingProjectId(project.id);
    setAcceptInviteError(null);
    try {
      await acceptInvitation(project.id);
      setProjectsRefreshKey((key) => key + 1);
      openProject(project);
    } catch (error) {
      setAcceptInviteError(error instanceof Error ? error.message : "Unable to accept invitation");
    } finally {
      setAcceptingProjectId((current) => current === project.id ? null : current);
    }
  }, [openProject]);

  const selectRun = useCallback((runId: string, nodeId?: string | null) => {
    updateRoute({ view: activeView === "home" ? "workbench" : activeView, run: runId, nodes: nodeId ?? null, focus: null, panel: null, configTab: null, plot: null });
  }, [activeView, updateRoute]);

  const selectPlotPoint = useCallback((runId: string | null, nodeId: string | null) => {
    updateRoute({ run: runId ?? selectedRunId, nodes: nodeId ?? null });
  }, [selectedRunId, updateRoute]);

  const openArtifact = useCallback((assetId: string) => {
    const asset = data.assets.find((item) => item.id === assetId);
    const rowId = asset?.refs.rows?.[0] ?? selectedRunId;
    if (asset?.type === "uvf") {
      updateRoute({
        view: "workbench",
        focus: "flowfield",
        artifact: assetId,
        run: rowId ?? null,
        nodes: asset.provenanceNodeId ?? asset.id,
        panel: null,
        configTab: null,
        plot: null,
      });
      return;
    }
    updateRoute({ view: "workbench", focus: "artifact", artifact: assetId, run: rowId ?? null, panel: null, configTab: null, plot: null });
  }, [data.assets, selectedRunId, updateRoute]);

  const activateLineageRecord = useCallback((id: string, node?: DagViewerNode) => {
    const operationRunId = operationRunIdFromLineageActivation(id, node);
    if (operationRunId) {
      selectRun(operationRunId, node?.id ?? `operation_run:${operationRunId}`);
      return;
    }
    updateRoute({
      view: activeView === "home" ? "workbench" : activeView,
      nodes: node?.id ?? id,
      run: null,
      artifact: null,
      focus: null,
      panel: null,
      configTab: null,
      plot: null,
    });
  }, [activeView, selectRun, updateRoute]);

  // FXC-8990: navigate to a global-search hit. Runs go to the workbench with
  // the row selected; artifact-like nodes open the artifact focus when we hold
  // a matching asset; otherwise fall back to selecting the provenance node so
  // the click is never a no-op.
  const handleGlobalSearchSelect = useCallback((result: SearchResult) => {
    const nodeId = String(result.node_id);
    if (result.node_type === "run") {
      selectRun(nodeId, nodeId);
      return;
    }
    const matchingAsset = data.assets.find(
      (asset) => asset.id === nodeId || asset.provenanceNodeId === nodeId,
    );
    if (matchingAsset) {
      openArtifact(matchingAsset.id);
      return;
    }
    updateRoute({ view: "workbench", nodes: nodeId, focus: null, panel: null, configTab: null, plot: null });
  }, [data.assets, openArtifact, selectRun, updateRoute]);

  const openFocus = useCallback((nextFocus: V5Focus) => {
    if (nextFocus === "notebook") {
      updateRoute({ drawer: drawerFromSearchParams(searchParams) === "spool" ? null : "spool", panel: null, configTab: null, plot: null });
      return;
    }
    updateRoute({ view: "workbench", focus: nextFocus, panel: null, configTab: null, plot: null });
  }, [searchParams, updateRoute]);

  const setPlotLarge = useCallback((large: boolean) => {
    updateRoute({ plot: large ? "large" : null });
  }, [updateRoute]);

  const openDashboard = useCallback((view: ThreadV4ViewSpec) => {
    const selected = typeof view.binding_json.selected_run_id === "string" ? view.binding_json.selected_run_id : null;
    const mode = view.layout_json.widgets.some((widget) => widget.kind === "plot") ? "correlogram" : "table";
    updateRoute({ view: "workbench", focus: null, run: selected, nodes: null, dashboard: view.id, mode });
  }, [updateRoute]);

  const saveDashboard = useCallback((request: DashboardSaveRequest) => {
    // FXC-8719: anonymous public viewers cannot persist views — createThreadV4View
    // would 401 and the .catch fallback used to fabricate a phantom local view.
    if (isAnonymous) return;
    const localView = makeDashboardView(projectId, branch, request.name, selectedRunId, {
      layout_json: request.layout_json,
      widget_state_json: request.widget_state_json,
      binding_json: request.binding_json,
      metadata: request.metadata,
    });
    // FXC-8690: when saving as part of "Save and leave", the captured pendingNav owns the
    // navigation — so skip the post-save openDashboard redirect that would pull us back.
    const redirectToDashboard = (view: ThreadV4ViewSpec) => {
      if (suppressDashboardRedirectRef.current) {
        suppressDashboardRedirectRef.current = false;
        return;
      }
      openDashboard(view);
    };
    const updateExisting = Boolean(request.updateExisting && request.sourceViewId);
    if (!persistedProject) {
      const next = updateExisting
        ? views.map((view) => view.id === request.sourceViewId ? { ...view, ...localView, id: view.id, slug: view.slug, created_at: view.created_at, updated_at: new Date().toISOString() } : view)
        : [localView, ...views];
      setViews(next);
      writeLocalViews(projectId, branch, next);
      redirectToDashboard(updateExisting ? next.find((view) => view.id === request.sourceViewId) ?? localView : localView);
      return;
    }
    if (updateExisting && request.sourceViewId && isUuidLike(request.sourceViewId)) {
      updateThreadV4View(request.sourceViewId, {
        name: request.name,
        layout_json: request.layout_json,
        widget_state_json: request.widget_state_json ?? {},
        binding_json: request.binding_json ?? {},
        metadata: request.metadata ?? {},
      })
        .then((updated) => {
          setViews((current) => current.map((view) => view.id === updated.id ? updated : view));
          redirectToDashboard(updated);
        })
        .catch(() => {
          const next = [localView, ...views];
          setViews(next);
          redirectToDashboard(localView);
        });
      return;
    }
    createThreadV4View({
      project_id: projectId,
      branch,
      name: request.name,
      slug: localView.slug,
      category: "custom",
      visibility: "project",
      layout_json: request.layout_json,
      binding_json: request.binding_json ?? localView.binding_json,
      widget_state_json: request.widget_state_json ?? {},
      metadata: localView.metadata,
    })
      .then((created) => {
        setViews((current) => [created, ...current.filter((view) => view.id !== created.id)]);
        redirectToDashboard(created);
      })
      .catch(() => {
        const next = [...views, localView];
        setViews(next);
        redirectToDashboard(localView);
      });
  }, [branch, isAnonymous, openDashboard, persistedProject, projectId, selectedRunId, views]);

  const addNotebookEntry = useCallback(() => {
    const body = notebookDraft.trim();
    if (!body) return;
    const localEntry = makeLocalNotebookEntry(projectId, branch, body, selectedRunId);
    setNotebookDraft("");
    if (!persistedProject) {
      const next = [localEntry, ...notebookEntries];
      setNotebookEntries(next);
      writeLocalNotebook(projectId, branch, next);
      return;
    }
    createThreadV4NotebookEntry({
      project_id: projectId,
      branch,
      entry_type: "note",
      author_label: "You",
      visibility: "work_shared",
      source_type: "manual",
      body,
      body_format: "markdown",
      anchors: selectedRunId ? { run_id: selectedRunId } : { project_id: projectId },
      metadata: { surface: "thread-v5" },
    })
      .then((entry) => setNotebookEntries((current) => [entry, ...current]))
      .catch(() => setNotebookEntries((current) => [localEntry, ...current]));
  }, [branch, notebookDraft, notebookEntries, persistedProject, projectId, selectedRunId]);

  const openFlow360 = useCallback(() => {
    const params = new URLSearchParams();
    if (persistedProject) params.set("thread_project_id", projectId);
    params.set("return_to", `${location.pathname}${location.search}`);
    navigate(`/flow360?${params.toString()}`);
  }, [location.pathname, location.search, navigate, persistedProject, projectId]);

  const activeDashboardView = useMemo(() => {
    if (!activeDashboardName) return null;
    return routeScopedViews.find((view) => view.id === activeDashboardName || view.slug === activeDashboardName || view.name === activeDashboardName) ?? null;
  }, [activeDashboardName, routeScopedViews]);
  // Keep the ref in sync so leave-guard callbacks (declared earlier) can read the current viewId.
  activeDashboardViewIdRef.current = activeDashboardView?.id;
  const activeDashboardDisplayName = activeDashboardView?.name ?? activeDashboardName;
  // V5 demo tours (?tour=<id>, optional ?kiosk=1). The live ref is refreshed
  // every render so tour challenge validators read current workbench state
  // (the highliftTour pattern — no context, no staleness under Vite dev).
  tourLiveRef.current = { selectedRunId, focus, activeDashboard: activeDashboardView?.slug ?? null };
  const tourParam = searchParams.get("tour");
  const tourKiosk = searchParams.get("kiosk") === "1";
  // The tour's loading veil lifts on THIS signal, not a DOM poll: the dashboard
  // the tour stages on has resolved, the run table has rows, and the chart the
  // first steps narrate has plot data. Covers the slow+variable part (the V3
  // data + plot load); the widget-internal fetches after it are fast.
  const tourStageReady =
    Boolean(activeDashboardView) &&
    data.rows.length > 0 &&
    data.assets.some((asset) => asset.type === "plot");
  const dagWorkspaceState = useMemo(
    () => parseDagWorkspaceState(searchParams.toString()),
    [searchParams],
  );
  const exactTemporalLineageCutActive = (
    activeView === "lineage" || activeView === "provenance"
  ) && dagWorkspaceState.temporalError === null
    && dagWorkspaceState.asOf !== null
    && dagWorkspaceState.recordedAsOf !== null
    && dagWorkspaceState.snapshotToken !== null;
  // FXC-8719: anonymous viewers get no drawers (notes/chat are write surfaces)
  // and no realtime presence/chat (both require an authed session).
  const routeDrawer = isAnonymous ? null : drawerFromSearchParams(searchParams);
  const temporalCompatibilityPaneRequested = exactTemporalLineageCutActive
    && (routeDrawer !== null || focus !== null);
  const notebookOpen = routeDrawer === "spool" && !exactTemporalLineageCutActive;
  const realtimeEnabled = Boolean(
    persistedProject
    && activeView !== "projects"
    && !isAnonymous
    && !exactTemporalLineageCutActive
  );
  const chatOpen = routeDrawer === "chat" && realtimeEnabled;
  const chatRealtimeEnabled = chatOpen;
  const flowfieldPair = useMemo(
    () => flowfieldPairFromSearchParams(searchParams, activeView, tableMode, focusedAssetId),
    [activeView, focusedAssetId, searchParams, tableMode],
  );
  const showFocusedArtifactLineage =
    focus === "artifact" &&
    searchParams.get("lineage") === "1" &&
    searchParams.get("graph") === "1";
  const presenceViewId = useMemo(() => {
    const dashboard = activeDashboardView?.id ?? activeDashboardName ?? "default";
    return `v5:${activeView}:${dashboard}:${selectedRunId ?? "project"}`;
  }, [activeDashboardName, activeDashboardView?.id, activeView, selectedRunId]);
  const presenceSelection = useMemo<Record<string, unknown>>(() => ({
    view: activeView,
    branch,
    selected_run_id: selectedRunId,
    selected_node_id: selectedNodeId,
    focused_asset_id: focusedAssetId,
    dashboard: activeDashboardDisplayName,
  }), [activeDashboardDisplayName, activeView, branch, focusedAssetId, selectedNodeId, selectedRunId]);
  const presence = useThreadPresence({
    projectId,
    enabled: realtimeEnabled,
    viewId: presenceViewId,
    selection: presenceSelection,
  });
  const projectChat = useProjectChat({
    projectId,
    enabled: chatRealtimeEnabled,
    open: chatOpen,
  });
  const flow360Connector = useMemo<Flow360ConnectorSummary>(() => {
    const browserSession = typeof window !== "undefined" && Boolean(getFlow360AccessToken());
    const storedKey = Boolean(flow360AuthStatus?.has_stored_key) || storedFlow360Flag("flow360_api_key_stored");
    const connectedToFlow360 = Boolean(flow360AuthStatus?.authenticated || browserSession || storedKey);
    const email = getFlow360UserEmail() ?? flow360AuthStatus?.user?.email ?? null;
    const statusLabel = storedKey
      ? "Thread credential"
      : browserSession
        ? "browser session"
        : connectedToFlow360
          ? "connected"
          : "connect";
    const detail = storedKey
      ? "Flow360 auth is minted into the Thread profile and available across Thread surfaces."
      : browserSession
        ? "This browser tab has a Flow360 session; Thread can mint it into the shared connector credential."
        : "Connect Flow360 once so Thread can import, refresh, fork, sweep, and run Filament/Tailor workflows.";
    return {
      connected: connectedToFlow360,
      browserSession,
      storedKey,
      email,
      statusLabel,
      detail,
    };
  }, [flow360AuthStatus, flow360ConnectorRefreshKey]);
  const signInFromPublicProject = () => {
    const destination = routeProject
      ? `/v5/${encodeURIComponent(routeProject)}${location.search}${location.hash}`
      : `${location.pathname}${location.search}${location.hash}`;
    sessionStorage.setItem("thread.post_auth_redirect", destination);
    navigate("/sign-in");
  };
  const toggleNotebook = useCallback(() => {
    updateRoute({ drawer: notebookOpen ? null : "spool" });
  }, [notebookOpen, updateRoute]);
  const toggleChat = useCallback(() => {
    updateRoute({ drawer: chatOpen ? null : "chat" });
  }, [chatOpen, updateRoute]);
  const sendProjectChat = useCallback(async () => {
    const sent = await projectChat.sendMessage(chatDraft);
    if (sent) setChatDraft("");
  }, [chatDraft, projectChat]);

  const surfaceProps = useMemo(() => ({
    data,
    dataLoading: v3State.status === "loading",
    projectId,
    projectName,
    branch,
    persistedProject,
    selectedRunId,
    selectedNodeId,
    focusedAssetId,
    tableMode,
    activeDashboardName: activeDashboardDisplayName,
    currentView: activeDashboardView,
    views: routeScopedViews,
    notebookEntries: routeScopedNotebookEntries,
    onTableModeChange: (mode: V5TableMode) => {
      setTableMode(mode);
      updateRoute({ mode });
    },
    onSelectRun: selectRun,
    onOpenArtifact: openArtifact,
    onOpenFocus: openFocus,
    onOpenFlow360: openFlow360,
    onOpenView: openView,
    onSwitchBranch: switchBranch,
    onOpenDashboard: openDashboard,
    onSaveDashboard: saveDashboard,
    onDashboardDirtyChange: setDashboardDirty,
    onRegisterDashboardSave: registerDashboardSave,
  }), [activeDashboardDisplayName, activeDashboardView, branch, data, focusedAssetId, openArtifact, openDashboard, openFlow360, openFocus, openView, switchBranch, persistedProject, projectId, projectName, registerDashboardSave, routeScopedNotebookEntries, routeScopedViews, saveDashboard, selectRun, selectedNodeId, selectedRunId, tableMode, updateRoute, v3State.status]);

  // FXC-8719: account-only views fall back to the workbench for anonymous viewers.
  const effectiveView = isAnonymous && ANON_BLOCKED_VIEWS.has(activeView) ? "workbench" : activeView;
  const surface = (() => {
    switch (effectiveView) {
      case "projects":
        return (
          <ProjectsSurface
            projects={projects}
            loading={projectsLoading}
            error={projectsError}
            currentProjectId={routeProject ?? null}
            onOpenProject={openProject}
            onOpenLineageLab={openLineageLab}
            onAcceptInvitation={acceptListedProjectInvite}
            onRefresh={() => setProjectsRefreshKey((key) => key + 1)}
            onOpenFlow360={openFlow360}
            acceptingProjectId={acceptingProjectId}
            acceptError={acceptInviteError}
          />
        );
      case "home":
        return <HomeSurface {...surfaceProps} />;
      case "data":
        return <DataSurface {...surfaceProps} />;
      case "artifacts":
        return <ArtifactsSurface {...surfaceProps} />;
      // "provenance" remains as a stale-URL alias for the canonical lineage viewer.
      case "provenance":
      // FXC-8630: full-surface lineage view (Warp as first-class V5 nav entry).
      case "lineage":
        if (slugResolvePending) {
          return (
            <div className="v5-loading-panel" aria-busy="true" data-testid="v5-lineage-project-resolving">
              <span className="v5-live is-pending"><i />resolving project</span>
              <h2>Resolving project slug</h2>
              <p>Looking up the saved Thread project before loading canonical lineage.</p>
            </div>
          );
        }
        if (persistedProject && (v3State.status === "loading" || data.project?.id !== projectId)) {
          return (
            <div className="v5-loading-panel" aria-busy="true" data-testid="v5-lineage-metadata-loading">
              <span className="v5-live is-pending"><i />loading lineage</span>
              <h2>Loading project metadata</h2>
              <p>Resolving the active branch before requesting canonical lineage.</p>
            </div>
          );
        }
        if (persistedProject || searchParams.has("labSize")) {
          const requestedPerfCount = Number(searchParams.get("dagPerf"));
          const syntheticNodeCount = import.meta.env.DEV && (requestedPerfCount === 10_000 || requestedPerfCount === 50_000)
            ? requestedPerfCount
            : null;
          if (!syntheticNodeCount) {
            return (
              <LineageWorkspaceSurface
                projectId={projectId}
                projectName={projectName}
                branch={branch}
                selectedNodeId={selectedNodeId}
                onSelectNode={(nodeId) => updateRoute({ nodes: nodeId })}
                onActivateNode={activateLineageRecord}
              />
            );
          }
          return (
            <DagViewerSurface
              projectId={projectId}
              projectName={projectName}
              branch={branch}
              selectedNodeId={selectedNodeId}
              onSelectNode={(nodeId) => updateRoute({ nodes: nodeId })}
              onActivateNode={activateLineageRecord}
              syntheticNodeCount={syntheticNodeCount}
            />
          );
        }
        if (exactTemporalLineageCutActive) {
          return (
            <div className="v5-loading-panel" data-testid="v5-temporal-compatibility-source-guard" role="status">
              <span className="v5-live is-pending"><i />current-only source</span>
              <h2>Exact temporal replay unavailable</h2>
              <p>This compatibility lineage source is current-only. Open a saved Thread project to replay an exact historical cut.</p>
            </div>
          );
        }
        return <LineageFullSurface {...surfaceProps} />;
      case "interpolant-audit":
        return <InterpolantAuditSurface projectName={projectName} branch={branch} selectedRunId={selectedRunId} />;
      case "selvedge":
        return <SelvedgeSurface isAnonymous={isAnonymous} />;
      case "kestrel-graph":
        return <KestrelGraphSurface />;
      case "branching":
        return <BranchingSurface {...surfaceProps} />;
      case "tailor":
        return <AdbExplorerSurface {...surfaceProps} />;
      // Composite converged into ADB Generator (TRANSPORT.md §7): the ADB
      // surface auto-detects composite-resident projects. The ?view=composite
      // param is kept as a back-compat alias onto the same surface.
      case "composite":
        return <AdbExplorerSurface {...surfaceProps} />;
      case "training":
        return <TrainingSurface {...surfaceProps} />;
      case "highlift":
        // Internal Flexcompute tooling — gated to @flexcompute.com (the API gates it
        // too; this avoids non-Flex users loading a surface that would only 403).
        return (
          <FlexcomputeOnlyGuard>
            <HighliftMultifidelitySurface {...surfaceProps} />
          </FlexcomputeOnlyGuard>
        );
      case "parametric-geometry":
        return (
          <ParametricGeometrySurface
            projectId={projectId}
            projectName={projectName}
            branch={branch}
            persistedProject={persistedProject}
          />
        );
      case "airshow-coverage":
        return (
          <AirshowCoverageSurface
            branch={branch}
            projectId={projectId}
            persistedProject={persistedProject}
          />
        );
      case "wing-section":
        return (
          <FlexcomputeOnlyGuard>
            {slugResolvePending ? (
              <div className="v5-loading-panel" aria-busy="true">
                <span className="v5-live is-pending"><i />resolving project</span>
                <h2>Resolving project slug</h2>
                <p>Looking up the saved Thread project before enabling wing slicing and carving.</p>
              </div>
            ) : (
              <WingSectioningSurface {...surfaceProps} />
            )}
          </FlexcomputeOnlyGuard>
        );
      case "aircraft":
        // Aircraft geometry workbench — fast exact-Bezier OpenVSP path. Gated to
        // Flexcompute like the other geometry-design surfaces.
        return (
          <FlexcomputeOnlyGuard>
            <AircraftSurface {...surfaceProps} />
          </FlexcomputeOnlyGuard>
        );
      case "control-surfaces":
        // Control-surface injection workbench (segment CAD, design the cove live,
        // bake the cut). Gated to Flexcompute like the other geometry-design surfaces.
        return (
          <FlexcomputeOnlyGuard>
            <ControlSurfacesSurface {...surfaceProps} />
          </FlexcomputeOnlyGuard>
        );
      case "filament-model":
        return (
          <AppacWakeWorkbench
            embedded
            projectId={projectId}
            projectName={projectName}
            branch={branch}
            aircraftDesignId={searchParams.get("aircraft") ?? undefined}
            onAircraftDesignChange={selectFilamentAircraft}
          />
        );
      case "flow360-import":
        return <Flow360ImportSurface {...surfaceProps} />;
      case "members":
        return <ProjectMembersSurface {...surfaceProps} />;
      case "dashboard-edit":
        return <DashboardEditorSurface {...surfaceProps} />;
      case "workbench":
      default:
        if (activeDashboardView) return <DashboardViewSurface {...surfaceProps} />;
        return <WorkbenchSurface {...surfaceProps} />;
    }
  })();

  const connected = lineageLabProject || (v3State.status === "ready" && (data.rows.length > 0 || data.errors.length === 0));

  // Shared between the desktop and mobile shells: the active surface, the
  // notebook/chat drawers, and the provenance focus layer (which owns the
  // plot-point → provenance interaction). Both shells render the identical
  // content so the workbench data and handlers behave the same everywhere.
  const content = (
    <>
      {surface}
      {temporalCompatibilityPaneRequested ? (
        <div className="v5-temporal-compatibility-notice" data-testid="v5-temporal-compatibility-notice" role="status">
          Current-only workspace panes are hidden while this exact temporal lineage cut is active.
        </div>
      ) : null}
      {notebookOpen ? (
        <NotebookDrawer
          selectedRunId={selectedRunId}
          entries={routeScopedNotebookEntries}
          draft={notebookDraft}
          onDraftChange={setNotebookDraft}
          onAdd={addNotebookEntry}
          onClose={() => updateRoute({ drawer: null })}
        />
      ) : null}
      {chatOpen ? (
        <ProjectChatDrawer
          projectName={projectName}
          projectId={projectId}
          branch={branch}
          status={projectChat.status}
          streamStatus={projectChat.streamStatus}
          messages={projectChat.messages}
          draft={chatDraft}
          sending={projectChat.sending}
          typingUsers={projectChat.typingUsers}
          error={projectChat.error}
          onDraftChange={setChatDraft}
          onTyping={projectChat.notifyTyping}
          onSend={sendProjectChat}
          onClose={() => updateRoute({ drawer: null })}
        />
      ) : null}
      {!exactTemporalLineageCutActive ? (
        <FocusLayer
          focus={focus}
          flowfieldPair={flowfieldPair}
          data={data}
          projectId={projectId}
          persistedProject={persistedProject}
          selectedRunId={selectedRunId}
          selectedNodeId={selectedNodeId}
          focusedAssetId={focusedAssetId}
          showArtifactLineage={showFocusedArtifactLineage}
          tableMode={tableMode}
          plotLarge={plotSize === "large"}
          onPlotLargeChange={setPlotLarge}
          onClose={() => updateRoute({ focus: null, artifact: null, panel: null, configTab: null, plot: null })}
          onSelectRun={selectRun}
          onSelectPlotPoint={selectPlotPoint}
          onFullProvenance={() => updateRoute({ view: "lineage", focus: null, panel: null, plot: null })}
        />
      ) : null}
    </>
  );

  const startMenu = (
    <ThreadV5StartMenu
      activeView={effectiveView}
      activeDashboardName={activeDashboardName}
      dashboards={exactTemporalLineageCutActive ? [] : routeScopedViews}
      readOnly={isAnonymous}
      chatOpen={chatOpen}
      onOpenView={openView}
      onOpenDashboard={openDashboard}
      onOpenChat={!exactTemporalLineageCutActive && realtimeEnabled ? toggleChat : undefined}
      onSignIn={signInFromPublicProject}
    />
  );

  const shell = isMobile ? (
    <ThreadV5MobileShell
      projectName={activeView === "projects" && !routeProject ? "Projects" : projectName}
      readOnly={isAnonymous}
      temporalReplayActive={exactTemporalLineageCutActive}
      navigationMenu={startMenu}
      onSignIn={signInFromPublicProject}
    >
      <div className={`v5-content${notebookOpen || chatOpen ? " has-spool" : ""}${effectiveView === "filament-model" || effectiveView === "parametric-geometry" || effectiveView === "airshow-coverage" ? " v5-content-aero" : ""}`}>
        {content}
      </div>
    </ThreadV5MobileShell>
  ) : (
    <div className="v5-app" data-testid="v5-app-shell">
      <ThreadV5Shell
        navigationMenu={startMenu}
        projectName={activeView === "projects" && !routeProject ? "Projects" : projectName}
        branch={activeView === "projects" && !routeProject ? null : branch}
        projectId={activeView === "projects" && !routeProject ? null : projectId}
        temporalReplayActive={exactTemporalLineageCutActive}
        connected={connected}
        selectedRunId={(activeView === "projects" && !routeProject) || exactTemporalLineageCutActive ? null : selectedRunId}
        activeDashboardName={exactTemporalLineageCutActive ? null : activeDashboardDisplayName}
        notebookOpen={notebookOpen}
        userInitials={userInitials}
        flow360Connector={flow360Connector}
        presenceStatus={presence.status}
        presenceUsers={exactTemporalLineageCutActive ? [] : presence.peers}
        chatOpen={chatOpen}
        chatUnreadCount={projectChat.unreadCount}
        readOnly={isAnonymous}
        onSignIn={signInFromPublicProject}
        onSearchSelect={exactTemporalLineageCutActive ? undefined : handleGlobalSearchSelect}
        onClearSelection={() => updateRoute({ run: null, clearRun: true, nodes: null, artifact: null, focus: null, panel: null, configTab: null, plot: null })}
        onOpenBranching={() => openView("branching")}
        onOpenFlow360={openFlow360}
        onOpenNotebook={toggleNotebook}
        onOpenChat={realtimeEnabled ? toggleChat : undefined}
        onOpenMembers={openMembers}
        onOpenProjects={isAnonymous ? signInFromPublicProject : openProjects}
        onCreateDashboard={() => openView("dashboard-edit")}
      />
      <div className={`v5-content${notebookOpen || chatOpen ? " has-spool" : ""}${effectiveView === "filament-model" || effectiveView === "parametric-geometry" || effectiveView === "airshow-coverage" ? " v5-content-aero" : ""}`}>
        {content}
      </div>
    </div>
  );

  return (
    <ProvenanceProvider projectId={projectId} branch={branch}>
      <ViewStateProvider>
        <PlotDataProvider>
          {/* Spans both shells + the surface switch so a sampler campaign launched
              on the Training surface stays selectable when the explorer mounts,
              and survives desktop↔mobile shell swaps. */}
          <AdaptiveCampaignProvider>
            {/* Demo tours narrate current project/branch data. Unmounting the launcher
                also destroys an already-running overlay if an exact replay is entered. */}
            {!exactTemporalLineageCutActive ? (
              <V5TourLauncher tourId={tourParam} kiosk={tourKiosk} ready={persistedProject} stageReady={tourStageReady} applyRoute={updateRoute} live={tourLiveRef} />
            ) : null}
            {shell}
          </AdaptiveCampaignProvider>
          <DashboardLeaveGuardModal
            open={pendingNav !== null}
            onSave={handleLeaveGuardSave}
            onDiscard={handleLeaveGuardDiscard}
            onCancel={handleLeaveGuardCancel}
          />
        </PlotDataProvider>
      </ViewStateProvider>
    </ProvenanceProvider>
  );
}
